import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { 
  ChevronRight, 
  Clock, 
  Calendar, 
  ChevronDown, 
  ChevronUp,
  CheckCircle2,
  AlertTriangle,
  Lightbulb,
  ExternalLink,
  ArrowRight,
  FileText,
  Shield,
  Target,
  Building2,
  HelpCircle,
  Layers,
  Search,
  ClipboardCheck,
  Construction,
  Eye
} from 'lucide-react';

const HowToChooseGeotechnicalStudy = () => {
  const [activeSection, setActiveSection] = useState('');
  const [openFaq, setOpenFaq] = useState(null);

  // Table of contents
  const sections = [
    { id: 'section-1', title: "Pourquoi l'étude géotechnique est indispensable" },
    { id: 'section-2', title: "La norme NF P 94-500" },
    { id: 'section-3', title: "Les 5 missions G1 à G5 expliquées" },
    { id: 'section-4', title: "Tableau récapitulatif" },
    { id: 'section-5', title: "Les erreurs fréquentes à éviter" },
    { id: 'section-6', title: "Questions fréquentes" },
    { id: 'section-7', title: "Bien choisir, c'est aussi bien s'équiper" },
  ];

  const faqs = [
    {
      question: "Les missions G3 et G4 sont-elles obligatoires pour une maison individuelle ?",
      answer: "Non, elles ne sont pas imposées par la loi pour ce type de projet. Elles deviennent pertinentes dès que la complexité du site le justifie (terrain en pente, nappes, fondations profondes, soutènements). Leur absence n'est pas une irrégularité, mais elle transfère le risque d'exécution sur le maître d'ouvrage."
    },
    {
      question: "Un même bureau d'études peut-il réaliser toutes les missions ?",
      answer: "Pour les missions G1 à G3, rien ne l'interdit, et la continuité présente l'avantage d'une connaissance cumulée du site. En revanche, la G4 étant une mission de supervision indépendante, elle doit être confiée à un géotechnicien distinct."
    },
    {
      question: "Quelle est la durée de validité d'une étude G1 ou G2 ?",
      answer: "Une étude G1 ou G2 est généralement considérée comme valable une trentaine d'années, à condition que le terrain n'ait pas été remanié (terrassements, remblais) et que le projet reste conforme aux hypothèses initiales. Toute modification du projet ou du terrain peut nécessiter une mise à jour."
    }
  ];

  // Track scroll for active section
  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.scrollY + 150;
      for (const section of sections) {
        const element = document.getElementById(section.id);
        if (element) {
          const { offsetTop, offsetHeight } = element;
          if (scrollPosition >= offsetTop && scrollPosition < offsetTop + offsetHeight) {
            setActiveSection(section.id);
            break;
          }
        }
      }
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      const offset = 100;
      const elementPosition = element.getBoundingClientRect().top + window.pageYOffset;
      window.scrollTo({ top: elementPosition - offset, behavior: 'smooth' });
    }
  };

  // Schema.org JSON-LD
  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": "Comment choisir son étude géotechnique : guide des missions G1 à G5",
    "description": "Quelle étude géotechnique choisir pour votre projet ? Guide complet des missions G1, G2, G3, G4, G5 selon la norme NF P 94-500 pour bien fonder votre construction.",
    "image": "https://geots.consulting/og-images/guide-etude-geotechnique.jpg",
    "author": {
      "@type": "Organization",
      "name": "GEOTS Consulting"
    },
    "publisher": {
      "@type": "Organization",
      "name": "GEOTS Consulting",
      "logo": {
        "@type": "ImageObject",
        "url": "https://geots.consulting/logo.png"
      }
    },
    "datePublished": "2026-06-28",
    "dateModified": "2026-06-28"
  };

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqs.map(faq => ({
      "@type": "Question",
      "name": faq.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.answer
      }
    }))
  };

  return (
    <>
      <Helmet>
        <title>Comment choisir son étude géotechnique : guide des missions G1 à G5 | GEOTS</title>
        <meta name="description" content="Quelle étude géotechnique choisir pour votre projet ? Guide complet des missions G1, G2, G3, G4, G5 selon la norme NF P 94-500 pour bien fonder votre construction." />
        <link rel="canonical" href="https://geots.consulting/guides/comment-choisir-etude-geotechnique" />
        <meta property="og:title" content="Comment choisir son étude géotechnique : guide des missions G1 à G5" />
        <meta property="og:description" content="Quelle étude géotechnique choisir pour votre projet ? Guide complet des missions G1, G2, G3, G4, G5 selon la norme NF P 94-500." />
        <meta property="og:type" content="article" />
        <meta property="og:url" content="https://geots.consulting/guides/comment-choisir-etude-geotechnique" />
        <meta name="twitter:card" content="summary_large_image" />
        <script type="application/ld+json">{JSON.stringify(articleSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
      </Helmet>

      <div className="min-h-screen bg-gray-50">
        {/* Breadcrumb */}
        <div className="bg-white border-b">
          <div className="container mx-auto px-4 py-3">
            <nav className="flex items-center gap-2 text-sm text-gray-600">
              <Link to="/" className="hover:text-[#635BFF]">Accueil</Link>
              <ChevronRight className="w-4 h-4" />
              <Link to="/guides" className="hover:text-[#635BFF]">Guides</Link>
              <ChevronRight className="w-4 h-4" />
              <span className="text-[#0A2540] font-medium">Choisir son étude géotechnique</span>
            </nav>
          </div>
        </div>

        {/* Hero Section */}
        <header className="bg-gradient-to-br from-[#0A2540] to-[#1a3a5c] text-white py-16">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl">
              <div className="flex items-center gap-4 mb-6 flex-wrap">
                <span className="bg-[#635BFF] text-white text-xs font-bold px-3 py-1 rounded-full">
                  GUIDE COMPLET
                </span>
                <span className="text-gray-300 text-sm flex items-center gap-1">
                  <Calendar className="w-4 h-4" />
                  Mis à jour le 28 juin 2026
                </span>
                <span className="text-gray-300 text-sm flex items-center gap-1">
                  <Clock className="w-4 h-4" />
                  10 min de lecture
                </span>
              </div>
              <h1 className="text-4xl md:text-5xl font-bold mb-6 leading-tight">
                Comment choisir son étude géotechnique :
                <span className="text-[#00D4FF]"> le guide complet (missions G1 à G5)</span>
              </h1>
              <p className="text-xl text-gray-300 mb-8">
                Vous avez un projet de construction, une extension, ou des fissures inquiétantes sur un bâtiment existant ? 
                Avant de couler la moindre fondation, une question revient toujours : quelle étude géotechnique demander ? 
                Ce guide vous aide à choisir la bonne mission, au bon moment, sans payer pour une étude inadaptée.
              </p>
              <div className="flex flex-wrap gap-4">
                <Link 
                  to="/etude-geotechnique" 
                  className="bg-[#635BFF] hover:bg-[#5248e5] text-white px-6 py-3 rounded-lg font-semibold flex items-center gap-2 transition-colors"
                >
                  Demander une étude
                  <ArrowRight className="w-5 h-5" />
                </Link>
                <Link 
                  to="/annuaire/ingenierie-etudes-geotechniques" 
                  className="bg-white/10 hover:bg-white/20 text-white px-6 py-3 rounded-lg font-semibold flex items-center gap-2 transition-colors"
                >
                  Trouver un bureau d'études
                </Link>
              </div>
            </div>
          </div>
        </header>

        <div className="container mx-auto px-4 py-12">
          <div className="flex flex-col lg:flex-row gap-12">
            {/* Sidebar - Table of Contents */}
            <aside className="lg:w-72 flex-shrink-0">
              <div className="sticky top-24 bg-white rounded-xl shadow-sm border p-6">
                <h3 className="font-bold text-[#0A2540] mb-4 flex items-center gap-2">
                  <Target className="w-5 h-5 text-[#635BFF]" />
                  Sommaire
                </h3>
                <nav className="space-y-2">
                  {sections.map((section, index) => (
                    <button
                      key={section.id}
                      onClick={() => scrollToSection(section.id)}
                      className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors flex items-start gap-2 ${
                        activeSection === section.id
                          ? 'bg-[#635BFF]/10 text-[#635BFF] font-medium'
                          : 'text-gray-600 hover:bg-gray-100'
                      }`}
                    >
                      <span className="text-xs font-bold text-gray-400 mt-0.5">{index + 1}.</span>
                      {section.title}
                    </button>
                  ))}
                </nav>

                {/* Link to related guide */}
                <div className="mt-6 pt-6 border-t">
                  <p className="text-xs text-gray-500 mb-2">Guide connexe</p>
                  <Link 
                    to="/guides/comment-choisir-foreuse-geotechnique"
                    className="text-sm text-[#635BFF] hover:underline flex items-center gap-1"
                  >
                    <FileText className="w-4 h-4" />
                    Choisir une foreuse géotechnique
                  </Link>
                </div>
              </div>
            </aside>

            {/* Main Content */}
            <main className="flex-1 max-w-3xl">
              <article className="prose prose-lg max-w-none">
                
                {/* Section 1 */}
                <section id="section-1" className="mb-16">
                  <h2 className="text-3xl font-bold text-[#0A2540] mb-6 flex items-center gap-3">
                    <span className="bg-[#635BFF] text-white w-10 h-10 rounded-full flex items-center justify-center text-lg">1</span>
                    Pourquoi l'étude géotechnique est-elle indispensable ?
                  </h2>
                  
                  <p className="text-gray-700 mb-6">
                    Le sol est le <strong>premier matériau de toute construction</strong> — et le plus imprévisible. 
                    Un terrain mal connu est à l'origine d'une part importante des sinistres en construction, 
                    en particulier sur la maison individuelle. Argiles gonflantes, nappes d'eau, cavités, 
                    remblais hétérogènes : ces aléas, invisibles en surface, peuvent provoquer tassements, 
                    fissures et désordres structurels coûteux.
                  </p>

                  <p className="text-gray-700 mb-6">
                    L'étude géotechnique répond à <strong>trois enjeux concrets</strong> :
                  </p>

                  <div className="grid md:grid-cols-1 gap-4 mb-8">
                    {[
                      { icon: Shield, title: "Sécuriser la construction", desc: "En adaptant les fondations à la réalité du sol." },
                      { icon: Target, title: "Maîtriser les coûts", desc: "Une étude bien calibrée évite autant le surdimensionnement inutile que les reprises en sous-œuvre après sinistre." },
                      { icon: ClipboardCheck, title: "Clarifier les responsabilités", desc: "Entre maître d'ouvrage, bureau d'études et entreprise de travaux." },
                    ].map((item, i) => (
                      <div key={i} className="bg-white p-4 rounded-lg border flex items-start gap-3">
                        <div className="bg-[#635BFF]/10 p-2 rounded-lg">
                          <item.icon className="w-5 h-5 text-[#635BFF]" />
                        </div>
                        <div>
                          <h4 className="font-semibold text-[#0A2540]">{item.title}</h4>
                          <p className="text-sm text-gray-600">{item.desc}</p>
                        </div>
                      </div>
                    ))}
                  </div>

                  <p className="text-gray-700">
                    En France, toutes ces études sont encadrées par une norme de référence : la <strong>NF P 94-500</strong>.
                  </p>
                </section>

                {/* Section 2 */}
                <section id="section-2" className="mb-16">
                  <h2 className="text-3xl font-bold text-[#0A2540] mb-6 flex items-center gap-3">
                    <span className="bg-[#635BFF] text-white w-10 h-10 rounded-full flex items-center justify-center text-lg">2</span>
                    La norme NF P 94-500 : le cadre des missions géotechniques
                  </h2>

                  <p className="text-gray-700 mb-6">
                    Publiée par l'AFNOR, la norme NF P 94-500 définit et classe les différentes missions d'ingénierie géotechnique. 
                    Elle évite les confusions entre une étude « préliminaire », une étude « de conception », 
                    une étude « d'exécution » ou un « diagnostic ». C'est aujourd'hui la <strong>référence technique</strong> pour 
                    les maîtres d'ouvrage, les professionnels et les assureurs.
                  </p>

                  <p className="text-gray-700 mb-6">
                    Son principe central : <strong>les missions s'enchaînent</strong> au fil de la vie du projet, 
                    de la faisabilité jusqu'à l'exécution, chacune avec un niveau d'investigation et de responsabilité précis. 
                    On les désigne par les sigles <strong>G1, G2, G3, G4 et G5</strong>.
                  </p>

                  <div className="bg-[#00D4FF]/10 border border-[#00D4FF]/30 rounded-xl p-6 mb-8">
                    <Lightbulb className="w-8 h-8 text-[#00D4FF] mb-3" />
                    <p className="text-[#0A2540]">
                      <strong>À noter :</strong> la norme connaît une révision en 2026 qui renforce les exigences, 
                      notamment sur les notes de calcul (référence explicite aux Eurocodes) et sur les missions de suivi 
                      et de supervision. Les grands principes G1 à G5 restent toutefois la base à connaître.
                    </p>
                  </div>
                </section>

                {/* Section 3 - Les 5 missions */}
                <section id="section-3" className="mb-16">
                  <h2 className="text-3xl font-bold text-[#0A2540] mb-6 flex items-center gap-3">
                    <span className="bg-[#635BFF] text-white w-10 h-10 rounded-full flex items-center justify-center text-lg">3</span>
                    Les 5 missions géotechniques expliquées simplement
                  </h2>

                  {/* G1 */}
                  <div className="bg-white rounded-xl border-l-4 border-l-blue-500 shadow-sm p-6 mb-6">
                    <h3 className="text-xl font-bold text-[#0A2540] mb-2 flex items-center gap-2">
                      <Search className="w-6 h-6 text-blue-500" />
                      Mission G1 — Étude géotechnique préalable
                    </h3>
                    <p className="text-gray-700 mb-4">
                      C'est le <strong>point de départ</strong>. La G1 collecte les premières informations sur le site 
                      et identifie les risques géotechniques. Elle se déroule en deux phases : une étude de site (ES) 
                      et les principes généraux de construction (PGC).
                    </p>
                    <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-start gap-2 mb-4">
                      <AlertTriangle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                      <p className="text-sm text-red-800">
                        <strong>Important :</strong> la G1 ne permet PAS de dimensionner des fondations. 
                        Elle cadre le risque, elle ne conçoit pas l'ouvrage. C'est souvent la mission demandée 
                        lors de la vente d'un terrain à bâtir (cadre loi ELAN).
                      </p>
                    </div>
                    <p className="text-sm bg-gray-50 p-3 rounded-lg">
                      <strong>Pour qui ?</strong> Vendeurs de terrains, maîtres d'ouvrage en phase de faisabilité.
                    </p>
                  </div>

                  {/* G2 */}
                  <div className="bg-white rounded-xl border-l-4 border-l-green-500 shadow-sm p-6 mb-6">
                    <h3 className="text-xl font-bold text-[#0A2540] mb-2 flex items-center gap-2">
                      <Layers className="w-6 h-6 text-green-500" />
                      Mission G2 — Étude géotechnique de conception
                    </h3>
                    <p className="text-gray-700 mb-4">
                      C'est la <strong>mission centrale</strong> pour qui construit. La G2 approfondit les analyses 
                      et fournit les solutions techniques : type de fondations, dimensionnement, dispositions constructives. 
                      Elle se décline en deux phases : Avant-Projet (AVP) puis Projet (PRO).
                    </p>
                    <p className="text-gray-700 mb-4">
                      C'est la G2 qui dit concrètement <em>comment</em> fonder votre ouvrage. 
                      Sans plan de structure fourni au géotechnicien, elle peut conduire à des surcoûts.
                    </p>
                    <p className="text-sm bg-gray-50 p-3 rounded-lg">
                      <strong>Pour qui ?</strong> Tout maître d'ouvrage en phase de conception d'un bâtiment ou d'un ouvrage.
                    </p>
                  </div>

                  {/* G3 */}
                  <div className="bg-white rounded-xl border-l-4 border-l-yellow-500 shadow-sm p-6 mb-6">
                    <h3 className="text-xl font-bold text-[#0A2540] mb-2 flex items-center gap-2">
                      <Construction className="w-6 h-6 text-yellow-500" />
                      Mission G3 — Étude et suivi géotechniques d'exécution
                    </h3>
                    <p className="text-gray-700 mb-4">
                      La G3 intervient <strong>pendant le chantier</strong>. Elle est confiée à l'entreprise de travaux. 
                      Le géotechnicien valide les hypothèses de la G2, établit les notes de calcul d'exécution 
                      et adapte les préconisations face aux aléas rencontrés : nappe inattendue, hétérogénéité, affouillement.
                    </p>
                    <p className="text-sm bg-gray-50 p-3 rounded-lg">
                      <strong>Pour qui ?</strong> Entreprises de travaux, sur la base de la G2.
                    </p>
                  </div>

                  {/* G4 */}
                  <div className="bg-white rounded-xl border-l-4 border-l-orange-500 shadow-sm p-6 mb-6">
                    <h3 className="text-xl font-bold text-[#0A2540] mb-2 flex items-center gap-2">
                      <Eye className="w-6 h-6 text-orange-500" />
                      Mission G4 — Supervision géotechnique d'exécution
                    </h3>
                    <p className="text-gray-700 mb-4">
                      La G4 est une <strong>mission de contrôle indépendante</strong>. Elle supervise les études 
                      et le suivi d'exécution réalisés par un autre géotechnicien. Point clé : pour garantir l'objectivité, 
                      <strong> la G4 doit être confiée à un géotechnicien distinct</strong> de celui qui réalise la G2 et la G3.
                    </p>
                    <p className="text-sm bg-gray-50 p-3 rounded-lg">
                      <strong>Pour qui ?</strong> Ouvrages sensibles : bâtiments publics, infrastructures, génie civil, 
                      soutènements profonds, zones sismiques.
                    </p>
                  </div>

                  {/* G5 */}
                  <div className="bg-white rounded-xl border-l-4 border-l-red-500 shadow-sm p-6 mb-6">
                    <h3 className="text-xl font-bold text-[#0A2540] mb-2 flex items-center gap-2">
                      <AlertTriangle className="w-6 h-6 text-red-500" />
                      Mission G5 — Diagnostic géotechnique
                    </h3>
                    <p className="text-gray-700 mb-4">
                      La G5 s'applique lorsqu'un ouvrage existant <strong>présente des désordres</strong> : fissures, 
                      tassements différentiels, mouvements de terrain. Elle identifie les causes géotechniques et propose des solutions. 
                      C'est la mission la plus courante en cas de sinistre sécheresse (catastrophe naturelle) : 
                      son rapport est utilisable par les assureurs.
                    </p>
                    <p className="text-sm bg-gray-50 p-3 rounded-lg">
                      <strong>Pour qui ?</strong> Propriétaires confrontés à des fissures ou désordres, dans le cadre d'un sinistre.
                    </p>
                  </div>
                </section>

                {/* Section 4 - Tableau récapitulatif */}
                <section id="section-4" className="mb-16">
                  <h2 className="text-3xl font-bold text-[#0A2540] mb-6 flex items-center gap-3">
                    <span className="bg-[#635BFF] text-white w-10 h-10 rounded-full flex items-center justify-center text-lg">4</span>
                    Tableau récapitulatif : quelle mission pour quel besoin ?
                  </h2>

                  <div className="bg-white rounded-xl border overflow-hidden mb-8">
                    <table className="w-full">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-4 py-3 text-left text-sm font-semibold text-[#0A2540]">Votre situation</th>
                          <th className="px-4 py-3 text-left text-sm font-semibold text-[#0A2540]">Mission adaptée</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y">
                        {[
                          ["🏠 Je vends un terrain à bâtir", "G1"],
                          ["🏗️ Je conçois ma construction / mes fondations", "G2"],
                          ["👷 Mon chantier démarre, je dois suivre l'exécution", "G3"],
                          ["🔍 Ouvrage sensible nécessitant un contrôle indépendant", "G4"],
                          ["⚠️ Mon bâtiment a des fissures / désordres", "G5"],
                        ].map(([situation, mission], i) => (
                          <tr key={i} className="hover:bg-gray-50">
                            <td className="px-4 py-3 text-gray-700">{situation}</td>
                            <td className="px-4 py-3">
                              <span className="bg-[#635BFF] text-white px-3 py-1 rounded-full text-sm font-bold">
                                {mission}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </section>

                {/* Section 5 - Erreurs */}
                <section id="section-5" className="mb-16">
                  <h2 className="text-3xl font-bold text-[#0A2540] mb-6 flex items-center gap-3">
                    <span className="bg-[#635BFF] text-white w-10 h-10 rounded-full flex items-center justify-center text-lg">5</span>
                    Les erreurs fréquentes à éviter
                  </h2>

                  <div className="space-y-4">
                    {[
                      { title: "Confondre G1 et G2", desc: "La G1 identifie les risques mais ne dimensionne rien. Beaucoup de maîtres d'ouvrage croient à tort qu'une G1 suffit pour construire." },
                      { title: "Penser que « quelques sondages » suffisent", desc: "Pour une extension ou un projet, une étude normée est indispensable." },
                      { title: "Croire qu'une G2 remplace une G5 en cas de fissures", desc: "Ce sont deux missions différentes avec des objectifs distincts." },
                      { title: "Lancer un chantier sans G3 dans une zone à risque", desc: "Cela transfère tout le risque d'exécution sur le maître d'ouvrage." },
                      { title: "Lancer une G2 sans plan de structure", desc: "Source de surcoûts et de reprises d'études." },
                    ].map((error, i) => (
                      <div key={i} className="bg-red-50 border border-red-200 rounded-xl p-4 flex items-start gap-3">
                        <AlertTriangle className="w-6 h-6 text-red-500 flex-shrink-0 mt-0.5" />
                        <div>
                          <h4 className="font-bold text-red-800">{error.title}</h4>
                          <p className="text-red-700 text-sm">{error.desc}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </section>

                {/* Section 6 - FAQ */}
                <section id="section-6" className="mb-16">
                  <h2 className="text-3xl font-bold text-[#0A2540] mb-6 flex items-center gap-3">
                    <span className="bg-[#635BFF] text-white w-10 h-10 rounded-full flex items-center justify-center text-lg">6</span>
                    Questions fréquentes
                  </h2>

                  <div className="space-y-3">
                    {faqs.map((faq, i) => (
                      <div key={i} className="bg-white rounded-xl border overflow-hidden">
                        <button
                          onClick={() => setOpenFaq(openFaq === i ? null : i)}
                          className="w-full px-6 py-4 flex items-center justify-between text-left hover:bg-gray-50 transition-colors"
                        >
                          <span className="font-semibold text-[#0A2540]">{faq.question}</span>
                          {openFaq === i ? (
                            <ChevronUp className="w-5 h-5 text-gray-400" />
                          ) : (
                            <ChevronDown className="w-5 h-5 text-gray-400" />
                          )}
                        </button>
                        {openFaq === i && (
                          <div className="px-6 pb-4">
                            <p className="text-gray-700">{faq.answer}</p>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </section>

                {/* Section 7 - Conclusion */}
                <section id="section-7" className="mb-16">
                  <h2 className="text-3xl font-bold text-[#0A2540] mb-6 flex items-center gap-3">
                    <span className="bg-[#635BFF] text-white w-10 h-10 rounded-full flex items-center justify-center text-lg">7</span>
                    Bien choisir, c'est aussi bien s'équiper
                  </h2>

                  <p className="text-gray-700 mb-6">
                    Une étude géotechnique fiable repose sur des investigations de qualité : essais pressiométriques, 
                    pénétrométriques, sondages, instrumentation. La précision des résultats dépend directement 
                    du matériel utilisé et de son bon étalonnage.
                  </p>

                  <div className="bg-[#00D4FF]/10 border border-[#00D4FF]/30 rounded-xl p-6 mb-8">
                    <Lightbulb className="w-8 h-8 text-[#00D4FF] mb-3" />
                    <p className="text-[#0A2540]">
                      <strong>Besoin de matériel pour vos investigations ?</strong> Consultez notre guide complet 
                      sur le choix des foreuses géotechniques pour réaliser vos sondages et essais in-situ.
                    </p>
                    <Link 
                      to="/guides/comment-choisir-foreuse-geotechnique"
                      className="inline-flex items-center gap-2 text-[#635BFF] font-semibold mt-3 hover:underline"
                    >
                      <FileText className="w-4 h-4" />
                      Lire le guide : Comment choisir une foreuse géotechnique
                      <ArrowRight className="w-4 h-4" />
                    </Link>
                  </div>
                </section>

                {/* CTA Section */}
                <section className="bg-gradient-to-br from-[#635BFF] to-[#8B5CF6] rounded-2xl p-8 text-white mb-16">
                  <h2 className="text-2xl font-bold mb-4">🚀 Comment GEOTS Consulting peut vous aider</h2>
                  <p className="mb-6 text-white/90">
                    GEOTS met en relation maîtres d'ouvrage, bureaux d'études et entreprises spécialisées 
                    en géotechnique et fondations spéciales. Besoin d'un accompagnement pour cadrer votre étude de sol 
                    ou trouver le bon prestataire ?
                  </p>
                  <div className="bg-white/10 rounded-xl p-4 mb-6">
                    <p className="font-semibold">🎁 Offre de lancement</p>
                    <p className="text-white/90">
                      Les 100 premiers fournisseurs bénéficient de 6 mois Premium offerts. Sans engagement, sans carte bancaire.
                    </p>
                  </div>
                  <div className="flex flex-wrap gap-4">
                    <Link to="/calcul/prix-etude-de-sol" className="bg-white text-[#635BFF] px-6 py-3 rounded-lg font-semibold hover:bg-gray-100 transition-colors flex items-center gap-2">
                      Estimer le prix
                      <ArrowRight className="w-5 h-5" />
                    </Link>
                    <Link to="/etude-geotechnique" className="bg-white/20 text-white px-6 py-3 rounded-lg font-semibold hover:bg-white/30 transition-colors flex items-center gap-2">
                      Demander une étude
                      <ArrowRight className="w-5 h-5" />
                    </Link>
                  </div>
                </section>

                {/* Related Guides */}
                <section className="mb-8">
                  <h2 className="text-2xl font-bold text-[#0A2540] mb-6">📚 Pour aller plus loin</h2>
                  <div className="grid md:grid-cols-2 gap-4">
                    <Link 
                      to="/calcul/prix-etude-de-sol"
                      className="bg-white rounded-xl border p-4 hover:shadow-md transition-shadow"
                    >
                      <p className="font-medium text-[#0A2540] mb-2">💰 Estimer le prix d&apos;une étude de sol</p>
                      <span className="text-sm text-[#635BFF] flex items-center gap-1">
                        Utiliser l&apos;outil
                        <ArrowRight className="w-4 h-4" />
                      </span>
                    </Link>
                    <Link 
                      to="/guides/comment-choisir-foreuse-geotechnique"
                      className="bg-white rounded-xl border p-4 hover:shadow-md transition-shadow"
                    >
                      <p className="font-medium text-[#0A2540] mb-2">Comment choisir une foreuse géotechnique ?</p>
                      <span className="text-sm text-[#635BFF] flex items-center gap-1">
                        Lire le guide
                        <ArrowRight className="w-4 h-4" />
                      </span>
                    </Link>
                    <Link 
                      to="/guides/comment-choisir-fondations-speciales"
                      className="bg-white rounded-xl border p-4 hover:shadow-md transition-shadow"
                    >
                      <p className="font-medium text-[#0A2540] mb-2">Comment choisir ses fondations spéciales ?</p>
                      <span className="text-sm text-[#635BFF] flex items-center gap-1">
                        Lire le guide
                        <ArrowRight className="w-4 h-4" />
                      </span>
                    </Link>
                  </div>
                </section>

                {/* Footer note */}
                <div className="text-center text-gray-500 text-sm border-t pt-8">
                  <p>
                    Ce guide est fourni à titre informatif. Pour tout projet de construction ou en cas de désordres, 
                    faites appel à un bureau d'études géotechniques qualifié, qui définira la mission adaptée 
                    à votre situation conformément à la norme NF P 94-500.
                  </p>
                  <p className="mt-2">📅 Dernière mise à jour : 28 juin 2026</p>
                </div>
              </article>
            </main>
          </div>
        </div>
      </div>
    </>
  );
};

export default HowToChooseGeotechnicalStudy;
